import { connect } from 'react-redux';
import { Map } from 'immutable';
import { toggleModal } from '@/actions';
import moment from 'moment';
import { setRedirectParams } from '@/actions/search';
import * as types from 'constants/ActionTypes';
import { formatDuration } from 'utils/Helpers';
import { setProviderSortOrder } from '@/actions/providers';
import {
  setTripSortOrder,
  setTripFilter,
  setTripFilters,
  resetTripFilters,
} from '@/actions/tripFilters';
import { newPurchase, fetchTripsDetails } from '@/actions/purchase';
import findTrip from 'utils/trips/findTrip';
import parseQueryString from 'utils/queryString/parseQueryString';
import stringifyQueryString from 'utils/queryString/stringifyQueryString';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import {
  tripsLocationByProvider,
  filterTrips,
  needsDepartureAfterFilter,
  stopsFilter as stopsEvaluator,
} from 'utils/tripFilters';
// Same reason as in ProviderDeparturesContainer: the A/B reader reaches the store singleton and
// the GrowthBook instance, so it lives with the caller and `tripFilters` stays pure.
import { isPriceWithoutFeesVariantActive } from 'utils/trips/priceWithoutFeesExperimentSort';
import { getBaseTripDiscountPercent } from 'models/parseTrip';
import { resolveSpecialDiscountBannerProps } from 'utils/specialDiscount';
import {
  getRecommendedTripsExcludeList,
  getRecommendedTripsListBySearch,
  isRecommendedTripsEnabled,
} from 'utils/userPreferences';
import { generateDepartureUrl } from 'utils/urls';
import { getTripTerminalInfo } from 'utils/trips/parseTripDetails';
import { passengersNotAvailable } from 'utils/passengers';
import { getFromSelectedSeats } from 'utils/seats';
import { isOldSearchPassengersFormat } from 'utils/Reserbus';
import {
  matchesTripProvider,
  retrieveOpenTrip,
  resolveReturnTripScope,
} from 'utils/providers/filterProviders';
import normalizeSearchWidgetDateFormat from 'utils/normalizeSearchWidgetDateFormat';
import getSearchAnalyticsData from 'utils/search/getSearchAnalyticsData';
import hasTooManyFilters from 'utils/search/hasTooManyFilters';
import ProviderReturns from './ProviderReturns';

const isSearchPolling = (trips) =>
  !trips || trips.valueSeq().some((transportType) => transportType.get('isPolling'));

const isStopsFilterAvailable = (trips) =>
  trips &&
  trips.some((t) => stopsEvaluator(t, 'direct')) &&
  trips.some((t) => stopsEvaluator(t, 'multiple'));

const mapStateToProps = (state, ownProps) => {
  const {
    config,
    providers,
    search,
    trips,
    cities,
    terminals,
    tripFilters,
    siemprePlus,
    whitelabelConfig: { features, env },
    userPreferences,
    seatsBus,
  } = state;
  const { match, location } = ownProps;
  const {
    departureDate,
    returnDate,
    departureId,
    passengers = 'A1',
    providerId: paramsProviderId,
    isOpen,
  } = match.params;
  const {
    seenPrice = 0,
    providerId: queryProviderId,
    returnIsOpenTicket: rawReturnIsOpenTicket = false,
    departureIsOpenTicket: rawDepartureIsOpenTicket = false,
  } = parseQueryString(location.search);

  // Convert string query params to booleans to avoid "false" being truthy
  const queryDepartureIsOpenTicket = rawDepartureIsOpenTicket === 'true';
  const returnIsOpenTicket = rawReturnIsOpenTicket === 'true';

  // Detect if departure is open ticket based on the route
  const isOpenTicketRoute = location.pathname.includes('/open-ticket/');
  const hasReturnDate = Boolean(returnDate);
  const departureIsOpenTicket = queryDepartureIsOpenTicket || (isOpenTicketRoute && hasReturnDate);

  const places = { ...cities, ...terminals };
  const searchId = search.getIn(['return', 'id']);
  const dSearchId = search.getIn(['departure', 'id']);

  // Get departureTrip early to use its providerId for round trips if not in URL
  const departureTrip = findTrip(trips.get(dSearchId), 'bus', departureId)?.toJS();

  // For round trips, if providerId is not in URL, infer scope from the selected departure trip.
  // Precedence: any-line (no scope) > cross-line (transporter group) > single departure line.
  const { providerId, departureTransporterName } = resolveReturnTripScope({
    features,
    providerId: paramsProviderId || queryProviderId,
    departureTrip,
    hasReturnDate,
    returnIsOpenTicket,
  });

  const searchIsPolling = isSearchPolling(trips.get(searchId));
  const paymentPlansList = config.paymentPlans;
  const providerList = providers.getIn([searchId, 'list']);
  let provider = providerList && providerList.find((el) => el.get('id') === providerId);
  const dProviders = providers.getIn([dSearchId, 'list']);
  const departureProvider = dProviders && dProviders.find((el) => el.get('id') === providerId);
  const dTransportType = departureProvider && departureProvider.get('transportType');
  const sortBy = tripFilters.get('sortBy');
  const filters = tripFilters.get('filters');
  const departureTimeFilter = filters.get('departureTime');
  const dLocationFilter = filters.get('departureLocation');
  const aLocationFilter = filters.get('arrivalLocation');

  // Filters section
  const stopsFilter = filters.get('stops');
  const categoriesFilter = filters.get('categories');
  const tripOptionsFilter = filters.get('tripOptions');
  const activeDepartureFilter = Array.isArray(departureTimeFilter)
    ? !departureTimeFilter.includes('none')
    : departureTimeFilter !== 'none';

  const activeLocationFilter = dLocationFilter !== 'none' || aLocationFilter !== 'none';
  const activeCategoriesFilter = categoriesFilter.length;
  const activeTripOptionsFilter = tripOptionsFilter.length;

  const activeStopsFilter =
    typeof stopsFilter === 'string' ? stopsFilter !== 'none' : !stopsFilter.includes('none');
  const activeFilters =
    activeDepartureFilter ||
    activeLocationFilter ||
    activeStopsFilter ||
    activeCategoriesFilter ||
    activeTripOptionsFilter;

  const couponCode = search.get('couponCode');
  const discountedTripsByCoupon = search.getIn(['discountedTripsByCoupon', couponCode], []);

  const tripDateDeparture = departureTrip && moment(departureTrip.departure);
  const tripDepartureDuration = departureTrip && formatDuration(departureTrip.duration);
  const tripDate =
    tripDateDeparture &&
    tripDateDeparture.format(
      `ddd ${normalizeSearchWidgetDateFormat(env.searchWidget?.dateFormat, 'DD MMMM')}`,
    );
  const tripTime = tripDateDeparture && tripDateDeparture.format('LT');

  const originCity = departureTrip?.origin?.cityName;
  const destinationCity = departureTrip?.destination?.cityName;

  let transportType = provider && provider.get('transportType');

  let appliedFilters = filters;
  let filteredTrips;
  let tripList;
  let tripsWithoutFiltersAfterTime;
  let redirectToBrand;
  const needsAfterTimeFilter = needsDepartureAfterFilter(departureTrip);
  const needsSameOriginFilter = departureTrip && features.REDIRECT_SAME_SITE_ONLY;
  const departureAfterTime = departureTrip && departureTrip.arrival;

  if (needsAfterTimeFilter) {
    appliedFilters = filters.merge(Map({ departureAfterTime }));
  }

  if (!provider && departureProvider) {
    transportType = dTransportType;
  }

  const searchPassengers = search.get('passengers').toJS();
  if (needsSameOriginFilter) {
    // Exclude trips from different brand if departure has redirectTo
    // Will compare departure brand from redirectTo which can be either undefined or a string
    redirectToBrand = departureTrip?.redirectTo?.brand || true;
    appliedFilters = appliedFilters.merge(Map({ sameOrigin: redirectToBrand }));
  }

  // filterTrips reads transporterName from appliedFilters (see tripFilters.filterTripsBy).
  // Without this merge, return results would not be restricted to the departure transporter.
  if (departureTransporterName) {
    appliedFilters = appliedFilters.merge(Map({ transporterName: departureTransporterName }));
  }

  if (features.PROVIDERS_SELECTION_ENABLED) {
    tripList = trips.getIn([searchId, 'buses', 'trips']);
    filteredTrips = filterTrips(
      tripList,
      providerId,
      sortBy,
      appliedFilters,
      null,
      searchPassengers,
      isPriceWithoutFeesVariantActive,
    );
  } else {
    if (trips.getIn([searchId, 'buses', 'trips'])) {
      tripList = trips.getIn([searchId, 'buses', 'trips']).map((item) => item.set('type', 'bus'));
    }
    filteredTrips = filterTrips(
      tripList,
      providerId,
      sortBy,
      appliedFilters,
      null,
      searchPassengers,
      isPriceWithoutFeesVariantActive,
    );
    provider = Map({ name: null });
  }

  const filteredTripsId = filteredTrips.toJS().map((trip) => trip.id);
  // Trips that match the departure line or transporter but were hidden by secondary filters (time, stops, etc.).
  const tripsWithoutFilters = tripList?.filter(
    (trip) =>
      !filteredTripsId.includes(trip.get('id')) &&
      matchesTripProvider(trip, providerId, departureTransporterName),
  );

  tripsWithoutFiltersAfterTime = tripsWithoutFilters || [];
  tripsWithoutFiltersAfterTime = filterTrips(
    tripsWithoutFilters,
    providerId || null,
    sortBy,
    Map({
      ...(needsAfterTimeFilter && { departureAfterTime }),
      ...(needsSameOriginFilter && { sameOrigin: redirectToBrand }),
      ...(departureTransporterName && { transporterName: departureTransporterName }),
    }),
    null,
    undefined,
    isPriceWithoutFeesVariantActive,
  );

  let filteredTripsJS = filteredTrips.toJS();
  const openTrip = retrieveOpenTrip(filteredTrips, providerId, departureTransporterName);
  const noTrips = !filteredTrips.size || (filteredTrips.size === 1 && Boolean(openTrip));

  const busTrips = trips.getIn([searchId, 'buses', 'trips']);
  const areOpenTicketTrips = busTrips?.filter((t) => t.get('openTicket')).toJS();
  const returnRoundMethod = features.DISCOUNT_LABEL_ROUND_METHOD_ON_RETURN;

  // Special discount (Hot Sale / Festival de Viajes) banner.
  const { specialDiscountVariant, showSpecialDiscountBanner } = resolveSpecialDiscountBannerProps({
    features,
    brand: env.brand,
    tripList,
    pricingKey: 'roundTripPricing',
  });

  const tripsForLineInfo = tripList?.size && tripList.toJS()[0];
  const discountPercent = getBaseTripDiscountPercent({
    trip: tripsForLineInfo,
    way: 'return',
    isRoundTrip: true,
    returnRoundMethod,
  });

  // Passengers searched
  /**
   * If some passenger searched is not found in the trips, this passengers is saved to notify
   * that the passengers was not found in this route
   */
  const isOldPassengersFormat = isOldSearchPassengersFormat(passengers);
  const passengersNotFound =
    (!isOldPassengersFormat &&
      features.VALID_CATEGORIES?.length > 1 &&
      passengersNotAvailable({
        neededPassengers: searchPassengers,
        trips: filteredTripsJS,
      })) ||
    [];

  const { recommendedTrips } = userPreferences.toJS();
  // Same arrival guard as `RecommendedTripsList` (via `useRecommendedTrips`): the grouped
  // `TripList` renders this list, so it must pick the same recommendation and never one that
  // leaves before the departure trip arrives.
  const recommendedTripsForSearch = !isRecommendedTripsEnabled()
    ? []
    : getRecommendedTripsListBySearch({
        searchId,
        recommendedTrips,
        providerId,
        departureTripArrival: !departureTrip?.openTicket && departureTrip?.arrival,
        departureTransporterName,
      });
  const promoUrgencyTrips = filteredTripsJS;

  if (Object.keys(recommendedTripsForSearch).length) {
    filteredTripsJS = getRecommendedTripsExcludeList({
      recommendedTripsList: recommendedTripsForSearch,
      tripList: filteredTripsJS,
    });
  }

  const originId = search.get('originId');
  const destinationId = search.get('destinationId');
  const originTerminal = terminals[originId];
  const destinationTerminal = terminals[destinationId];

  // Get terminal info but swap origin and destination terminals for the return trip
  const tripTerminalInfo = getTripTerminalInfo({
    originTerminal: destinationTerminal,
    destinationTerminal: originTerminal,
  });

  const analyticsSearchData = getSearchAnalyticsData({
    search,
    places,
    departures: trips.get(dSearchId),
    returns: trips.get(searchId),
  }).search;

  // Get closed trips from backend response
  const closedTrips = trips.getIn([searchId, 'buses', 'closedTrips']);
  const closedTripsJs = closedTrips?.toJS() ?? [];

  // Validating if departure seats are saved, this is used for the results seats selection
  const hasValidDepartureSeats = departureTrip?.openTicket
    ? true
    : getFromSelectedSeats({
        seatsState: seatsBus.toJS(),
        fromId: departureId,
      });

  return {
    activeDepartureFilter,
    activeLocationFilter,
    activeStopsFilter,
    stopsFilter,
    categoriesFilter,
    tripOptionsFilter,
    departureDate,
    departureId,
    noTrips,
    passengers,
    returnDate,
    searchIsPolling,
    seenPrice,
    sortBy,
    transportType,
    openTrip,
    arrivalLocationFilter: aLocationFilter,
    currentPath: location.pathname,
    departureFilter: departureTimeFilter,
    departureLocationFilter: dLocationFilter,
    departureTrip: departureTrip || {},
    destination: places[search.getIn(['return', 'destinationId'])],
    hasMultipleProviders:
      features.PROVIDERS_SELECTION_ENABLED && search.get('hasMultipleProviders'),
    paymentPlans: paymentPlansList || [],
    installmentsMinAmount: config.installmentsMinAmount,
    locations: tripsLocationByProvider(tripList, providerId),
    origin: places[search.getIn(['return', 'originId'])],
    provider: provider && provider.toJS(),
    providers: providerList ? providerList.toJS() : [],
    roundTrip: !!returnDate,
    sortProvidersBy: providers.get('sortBy'),
    tooManyFilters: hasTooManyFilters({
      searchIsPolling,
      noTrips,
      activeFilters,
      brand: env.brand,
      tripList,
    }),
    trips: filteredTripsJS,
    recommendedTrips: recommendedTripsForSearch,
    promoUrgencyTrips,
    closedTrips: closedTripsJs,
    isStopsFilterAvailable: isStopsFilterAvailable(tripList),
    couponCode,
    discountedTripsByCoupon,
    isOpenTicketList: isOpen,
    isLogged: Boolean(siemprePlus.get('user')),
    providerId,
    areOpenTicketTrips,
    returnIsOpenTicket,
    departureIsOpenTicket,
    tripDepartureDuration,
    tripDate,
    tripTime,
    originCity,
    destinationCity,
    filters: filters.toJS(),
    tripsWithoutFilters: tripsWithoutFiltersAfterTime.toJS(),
    searchOriginId: search.get('originId'),
    searchDestinationId: search.get('destinationId'),
    discountPercent,
    resultPriceToShow: search.get('resultPriceToShow'),
    firstTripLineId: tripsForLineInfo?.lineId,
    firstTripLineLogo: tripsForLineInfo?.line?.logoUrl,
    specialDiscountVariant,
    showSpecialDiscountBanner,
    brand: env.brand,
    dateFormat: env.searchWidget?.dateFormat,
    passengersNotFound,
    tripTerminalInfo,
    passengersQueryParam: passengers,
    hasValidDepartureSeats,
    analyticsSearchData,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  showModal: (component, extraProps) => dispatch(toggleModal('visible', component, extraProps)),
  setSortOrder: (sortBy, list) =>
    list === 'providers'
      ? dispatch(setProviderSortOrder(sortBy))
      : dispatch(setTripSortOrder(sortBy)),
  setTripFilter: (filterBy, active) => dispatch(setTripFilter(filterBy, active)),
  setTripFilters: (filter) => dispatch(setTripFilters(filter)),
  resetTripFilters: () => dispatch(resetTripFilters()),
  transitionTo: (route, searchQuery) => {
    const queryString = stringifyQueryString(searchQuery, ownProps.location.search);
    ownProps.history.push(`${route}${queryString}`);
  },
  replaceUrl: (route, searchQuery) => {
    const queryString = stringifyQueryString(searchQuery, ownProps.location.search);
    ownProps.history.replace(`${route}${queryString}`);
  },
  setRedirectParams: (way, params, brand, redirect, roundTrip, waitForPurchase) =>
    dispatch(setRedirectParams(way, params, brand, redirect, roundTrip, waitForPurchase)),

  redirectToDeparture: (hasProviderSelection) => {
    const { originSlug, destinationSlug, returnDate, departureDate, passengers } =
      ownProps.match.params;
    ownProps.history.push(
      generateDepartureUrl({
        originSlug,
        destinationSlug,
        departureDate,
        returnDate,
        passengers,
        useProviderSelection: hasProviderSelection,
      }),
    );
  },
  fetchTripsDetails: (trip) => dispatch(fetchTripsDetails(trip)),
  newPurchase: (originSlug, returnSlug, seenPrice) =>
    dispatch(newPurchase(originSlug, returnSlug, 'A1', seenPrice, null, false, {}, true, null, {})),

  updateDepartureIsOpen: (isOpen) => {
    dispatch({
      type: types.UPDATE_DEPARTURE_IS_OPEN,
      isOpen,
    });
  },
});

export default compose(
  withTranslation('trips'),
  connect(mapStateToProps, mapDispatchToProps),
)(ProviderReturns);
